import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { paymentApi, PaymentOrder } from '../../services/paymentApi';
import { PlanConfig } from '../../services/subscriptionService';
import './Subscription.css';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: PlanConfig | null;
  planKey: string | null;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  plan,
  planKey,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePayment = async () => {
    if (!user?.uid) {
      setError('Usuário não identificado');
      return;
    }
    if (!plan || !planKey) {
      setError('Plano não selecionado.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const order = await paymentApi<PaymentOrder>('create-checkout', {
        method: 'POST',
        body: { planType: planKey, displayedPrice: plan.price },
      });
      sessionStorage.setItem('pendingPaymentOrderId', order.orderId);
      window.location.assign(order.checkoutUrl);
    } catch (err: any) {
      console.error('Erro ao criar pagamento:', err);
      setError(err.message || 'Erro ao iniciar checkout. Tente novamente.');
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Assinar NutriSync"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handlePayment} disabled={loading}>
            {loading ? 'Processando...' : 'Ir para Pagamento'}
          </Button>
        </>
      }
    >
      <div className="ns-payment-modal-content">
        <div className="ns-payment-type-selection">
          <strong>Pagamento único</strong>
          <p>O acesso é liberado por 30 dias após confirmação do Mercado Pago.</p>
          <small>A renovação automática será reativada quando o processamento recorrente estiver migrado.</small>
        </div>

        <div className="ns-payment-plan">
          <h3>Plano {plan?.name}</h3>
          <div className="ns-payment-price">
            <span className="ns-payment-currency">R$</span>
            <span className="ns-payment-amount">
              {plan?.price.toFixed(2).replace('.', ',')}
            </span>
            <span className="ns-payment-period">/mês</span>
          </div>
        </div>

        <div className="ns-payment-features">
          <h4>Inclui:</h4>
          <ul>
            <li>✓ Acesso completo à plataforma</li>
            <li>✓ Gestão de <strong>{plan?.limit && plan.limit >= 9000 ? 'ilimitada' : plan?.limit}</strong> pacientes</li>
            <li>✓ Planos de refeição personalizados</li>
            <li>✓ Agenda e consultas</li>
            <li>✓ Relatórios e análises</li>
            <li>✓ Chat com pacientes</li>
            <li>✓ Suporte prioritário</li>
          </ul>
        </div>

        {error && (
          <div className="ns-payment-error">
            <p>{error}</p>
          </div>
        )}

        <div className="ns-payment-info">
          <p>
            Você será redirecionado ao checkout seguro do Mercado Pago. O pagamento só libera o acesso após confirmação no servidor.
          </p>
        </div>
      </div>
    </Modal>
  );
};
